import { useQueryClient } from "@tanstack/react-query";
import { type AnyRouter, useRouter } from "@tanstack/react-router";
import type React from "react";
import {
	createContext,
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { flushSync } from "react-dom";
import {
	adminApi,
	authApi,
	clearImpersonationMarker,
	ensureImpersonation,
	getAccessToken,
	getTokenExpiry,
	isEmbedded,
	readImpersonationMarker,
	refreshSession,
	setAccessToken,
	setCurrentUser,
	setImpersonationMarker,
	setTenantOverride,
	silentRefresh,
} from "@/lib/auth-api";
import type { LoginRequest, User } from "@/lib/auth-types";
import { assertPersistedCacheOwner, dropPersistedQueryCache } from "@/lib/query-persist";
import { syncSentryUser } from "@/lib/sentry";
import { SessionExpiredModal } from "./session-expired-modal";

interface AuthContextValue {
	user: User | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	isImpersonating: boolean;
	impersonationExpiresAt: number | null;
	impersonationStartedAt: number | null;
	login: (data: LoginRequest) => Promise<void>;
	logout: () => Promise<void>;
	refreshToken: () => Promise<void>;
	/**
	 * `targetTenantId` jest wymagany, gdy podgladany user nalezy do INNEGO tenanta
	 * niz operator (super_admin z przelaczonym tenantem). `users.user_id` to IDENTITY
	 * per-org, wiec bez tenanta backend trafia w przypadkowa osobe o tym samym id.
	 */
	startImpersonation: (targetUserId: number, targetTenantId?: number) => Promise<void>;
	stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Tożsamość, do której należą dane w cache: organizacja macierzysta + użytkownik.
 * `users.user_id` jest IDENTITY per schemat, więc samo id nie odróżnia osoby
 * z `prima` od osoby z `retiro`.
 */
function identityKey(user: User | null): string | null {
	return user ? `${user.tenantId}:${user.id}` : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient();
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [sessionExpired, setSessionExpired] = useState(false);
	const [impersonationExpiresAt, setImpersonationExpiresAt] = useState<number | null>(null);
	const [impersonationStartedAt, setImpersonationStartedAt] = useState<number | null>(null);
	const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// scheduleRefresh musi umiec zakonczyc podglad, a stopImpersonation jest
	// zdefiniowane nizej i samo wola scheduleRefresh — ref rozcina ten cykl.
	const stopImpersonationRef = useRef<(() => Promise<void>) | null>(null);
	// Ostatnia opublikowana tożsamość (nie zeruje się przy wylogowaniu), żeby
	// kolejne logowanie wiedziało, czy na ekranie mogą wisieć dane innej osoby.
	const lastIdentityRef = useRef<string | null>(null);
	const [identityEpoch, setIdentityEpoch] = useState(0);
	// W aplikacji AuthProvider siedzi w komponencie root route, więc zawsze pod
	// RouterProviderem. Testy jednostkowe montują go bez routera (null) — wtedy
	// zmiana osoby to sam remount.
	const router: AnyRouter | null = useRouter({ warn: false });
	const [identitySwitching, setIdentitySwitching] = useState(false);
	const switchSeqRef = useRef(0);

	/**
	 * Jedyna droga publikowania tożsamości (`user` + `_currentUser` guardów).
	 *
	 * `invalidateQueries` tylko oznacza dane jako stare: komponent dalej maluje
	 * wynik poprzedniej osoby, dopóki refetch nie wróci — a gdy padnie, maluje go
	 * nadal. Dlatego przy zmianie tożsamości cache znika z pamięci i z dysku, ZANIM
	 * React zobaczy nowego użytkownika; spóźnione odpowiedzi starych zapytań trafiają
	 * już do usuniętych obiektów `Query`.
	 *
	 *  - A → brak (wylogowanie, utrata sesji): czyszczenie bez remountu — strona
	 *    zaraz schodzi na login, a remount odpaliłby zapytania bez tokenu (401).
	 *  - A → B (inne konto, podgląd, koniec podglądu): czyszczenie, zdjęcie całego
	 *    drzewa z ekranu, ponowne przejście guardów trasy dla B i dopiero wtedy
	 *    montaż od zera — żaden komponent ani provider nie trzyma wyniku lub
	 *    lokalnego stanu A. Przepięcie organizacji wraca do macierzystej nowej osoby.
	 *  - A → A (odświeżenie tokenu, ponowne zalogowanie tej samej osoby): cache
	 *    zostaje.
	 *
	 * Sam remount NIE przechodzi `beforeLoad`: montował trasę, którą router miał
	 * wyrenderowaną, czyli stronę A, już pod B i z pustym cache — strona admina
	 * wysyłała /admin/* z tokenem podglądanej osoby (403, FARMASTER-FRONTEND-44).
	 * Dlatego przy A → B router najpierw rozstrzyga trasę dla B: `landing` (start
	 * i koniec podglądu) albo `invalidate()` bieżącej trasy, z przekierowaniem jak
	 * przy wejściu z linka. Promise rozwiązuje się, gdy router rozstrzygnął trasę
	 * i ponowny montaż drzewa jest zaplanowany (jeszcze przed jego commitem).
	 */
	const publishUser = useCallback(
		(next: User | null, landing?: "/dashboard"): Promise<void> => {
			const nextKey = identityKey(next);
			const lastKey = lastIdentityRef.current;
			const otherPerson = lastKey !== null && nextKey !== lastKey;
			if (otherPerson) {
				void queryClient.cancelQueries();
				queryClient.clear();
				dropPersistedQueryCache();
			}
			if (nextKey !== null) lastIdentityRef.current = nextKey;
			// Guardy czytają `_currentUser` i przepiętą organizację — muszą widzieć
			// nową osobę, zanim router je uruchomi.
			setCurrentUser(next);
			if (!otherPerson || next === null) {
				setUser(next);
				return Promise.resolve();
			}
			setTenantOverride(null);
			if (!router) {
				setUser(next);
				setIdentityEpoch((epoch) => epoch + 1);
				return Promise.resolve();
			}
			const seq = ++switchSeqRef.current;
			// flushSync: drzewo razem z cleanupem efektów schodzi z ekranu, ZANIM router
			// dostanie nawigację, więc strona A nie wyrenderuje się już pod B (po `clear()`
			// każdy render obserwatora bez `enabled: false` buduje nowe `Query` i wysyła
			// żądanie z tokenem B). To zabezpieczenie niezależne od harmonogramu Reacta i
			// żaden test go nie pilnuje: w React 19.2 zwykły setState też zdąży, bo
			// renderuje się razem z aktualizacją store'u routera (ta sama grupa lane'ów),
			// i auth-identity-switch-stale-route bez flushSync jest zielony. Wolno:
			// publishUser biegnie zawsze po `await`, nigdy synchronicznie w renderze ani
			// w efekcie.
			flushSync(() => {
				setIdentitySwitching(true);
				setUser(next);
			});
			// Start i koniec podglądu mają stały cel. Nie `invalidate()` + nawigacja
			// wołającego: spóźnione przekierowanie guarda (np. /403 po pobraniu reguł)
			// nadpisałoby jego nawigację.
			const settled = landing ? router.navigate({ to: landing }) : router.invalidate();
			return settled.finally(() => {
				// Nowsza zmiana osoby w międzyczasie — odsłoni drzewo sama.
				if (switchSeqRef.current !== seq) return;
				setIdentityEpoch((epoch) => epoch + 1);
				setIdentitySwitching(false);
			});
		},
		[queryClient, router],
	);

	const scheduleRefresh = useCallback(
		(expiresIn: number) => {
			if (refreshTimerRef.current) {
				clearTimeout(refreshTimerRef.current);
			}

			const delay = Math.max((expiresIn - 60) * 1000, 0);
			refreshTimerRef.current = setTimeout(async () => {
				// Tryb podgladu NIE odswieza sie po cichu. `/auth/refresh` mintuje token
				// przez issue_access_token (routes/auth.rs:435), ktore nie zna flagi
				// `impersonating` — sesja wrocilaby do pelnych uprawnien operatora przy
				// banerze, ktory wlasnie znikl, i przy zywym markerze w sessionStorage
				// (F5 wskrzeszalby podglad). Podglad konczy sie jawnie: przyciskiem albo
				// tutaj, po wyczerpaniu wlasnego TTL.
				if (readImpersonationMarker()) {
					const stop = stopImpersonationRef.current;
					if (stop) {
						await stop();
						return;
					}
					// Ref jeszcze nie podpiety (timer wyprzedzil efekt) — nie zostawiaj
					// sesji bez timera odswiezania, sprobuj ponownie za minute.
					scheduleRefresh(60);
					return;
				}
				try {
					const response = await authApi.refreshToken();
					setAccessToken(response.accessToken);
					const me = await authApi.getMe();
					void publishUser(me);
					scheduleRefresh(response.expiresIn);
				} catch {
					setAccessToken(null);
					publishUser(null);
				}
			}, delay);
		},
		[publishUser],
	);

	const login = useCallback(
		async (data: LoginRequest) => {
			const response = await authApi.login(data);
			setAccessToken(response.accessToken);
			const u = response.user as User;
			void publishUser(u);
			scheduleRefresh(response.expiresIn);
			// Cache z dysku należy do POPRZEDNIEJ sesji — w produkcie, w którym apteki
			// są dla siebie konkurencją, nie ma wersji „prawie właściwy użytkownik".
			dropPersistedQueryCache();
			// refetchType: "none" — mark stale but defer refetch to the next mount.
			// Auth-state setState above hasn't been committed by React yet; a refetch
			// now would fire with the new token but observers' stale `enabled` flags,
			// producing 403 floods on admin queries (Sentry FARMASTER-FRONTEND-1F).
			await queryClient.invalidateQueries({ refetchType: "none" });
		},
		[scheduleRefresh, queryClient, publishUser],
	);

	const logout = useCallback(async () => {
		try {
			await authApi.logout();
		} finally {
			clearImpersonationMarker();
			setAccessToken(null);
			publishUser(null);
			dropPersistedQueryCache();
			setTenantOverride(null);
			if (refreshTimerRef.current) {
				clearTimeout(refreshTimerRef.current);
			}
		}
	}, [publishUser]);

	const startImpersonation = useCallback(
		async (targetUserId: number, targetTenantId?: number) => {
			const response = await adminApi.startImpersonation(targetUserId, targetTenantId);
			setAccessToken(response.accessToken);
			const me = await authApi.getMe();
			// Podgląd cudzej sesji: cache operatora nie może przejść na podglądaną osobę,
			// a strona operatora nie może zamontować się pod nią bez guardów — router
			// ląduje na /dashboard, zanim drzewo wróci na ekran (FARMASTER-FRONTEND-44).
			const switched = publishUser(me, "/dashboard");
			dropPersistedQueryCache();
			// Marker niesie tenanta razem z id — po F5 re-impersonacja musi trafic
			// w TE SAMA osobe, a `user_id` jest unikalne tylko w obrebie schematu.
			// Poczatek podgladu tez tu ladu je: licznik "w gore" przezywa F5.
			const startedAt = Date.now();
			setImpersonationMarker({ userId: targetUserId, tenantId: targetTenantId }, startedAt);
			// Przepnij timer na TTL podgladu (2 h). Bez tego zyje termin z sesji
			// operatora (~14 min) i to on decyduje, kiedy podglad sie urwie.
			scheduleRefresh(response.expiresIn);
			setImpersonationExpiresAt(Date.now() + response.expiresIn * 1000);
			setImpersonationStartedAt(startedAt);
			// Mark ALL queries stale, but DO NOT eager-refetch (Sentry FARMASTER-FRONTEND-1F:
			// /admin/tenants, /admin/users, /admin/roles, /admin/pharmacy-groups,
			// /admin/visibility-rules all 403 within ~50 ms of impersonation start).
			// A plain invalidateQueries() would refetch every active observer against its
			// previous `enabled`/auth state with the NEW impersonated token. For a real
			// person change the cache is already empty and the tree is off screen until
			// the router lands on /dashboard, so this matters only when publishUser kept
			// the tree (same identity).
			await queryClient.invalidateQueries({ refetchType: "none" });
			// Visibility rules drive the entire sidebar + dashboard widgets — without
			// fresh data the impersonated user sees "Brak modułów" / "Brak dostępnych
			// widżetów" until manual refresh. The `me` and `me/org` endpoints are
			// scoped only to `isAuthenticated`, so eager-refetching them is safe (no
			// stale `enabled: super_admin` flag to misfire the way admin queries do).
			// Drop the admin rules cache outright — the impersonated user must not
			// inherit super_admin's editor view, and `useVisibilityRules()` is now
			// disabled anyway (isSuperAdmin → false). Mirrors the re-impersonation
			// path on page refresh below.
			queryClient.removeQueries({ queryKey: ["visibility-rules", "admin"] });
			void queryClient.invalidateQueries({ queryKey: ["visibility-rules", "me"] });
			await switched;
		},
		[queryClient, scheduleRefresh, publishUser],
	);

	const stopImpersonation = useCallback(async () => {
		try {
			// Marker znika PRZED requestem — inaczej retry po 401 wewnatrz
			// stopImpersonation odtworzylby wlasnie konczony podglad.
			clearImpersonationMarker();
			setImpersonationExpiresAt(null);
			setImpersonationStartedAt(null);
			await adminApi.stopImpersonation();
			const response = await authApi.refreshToken();
			setAccessToken(response.accessToken);
			const me = await authApi.getMe();
			// Tak samo jak na starcie: trasa podglądanej osoby nie montuje się pod
			// operatorem bez guardów (operator-KR nie musi mieć do niej grantu).
			// Także koniec z timera po TTL ląduje na /dashboard.
			const switched = publishUser(me, "/dashboard");
			dropPersistedQueryCache();
			scheduleRefresh(response.expiresIn);
			// Same race as startImpersonation — restored token + stale observer flags.
			// See the note above; `refetchType: "none"` keeps the invalidation intent
			// without flooding Sentry from queries scoped to the impersonated view.
			await queryClient.invalidateQueries({ refetchType: "none" });
			// Symmetric to startImpersonation: refetch visibility-rules so the sidebar
			// repopulates with the restored super_admin scope. The admin editor cache
			// was dropped on entry; on exit it's left to lazy-load when the editor
			// remounts (its observer's `enabled: isSuperAdmin` flips back to true).
			void queryClient.invalidateQueries({ queryKey: ["visibility-rules", "me"] });
			await switched;
		} catch {
			clearImpersonationMarker();
			await logout();
		}
	}, [scheduleRefresh, logout, queryClient, publishUser]);

	useEffect(() => {
		stopImpersonationRef.current = stopImpersonation;
	}, [stopImpersonation]);

	const refreshToken = useCallback(async () => {
		try {
			// refreshSession, NIE authApi.refreshToken — to samo /auth/refresh, ale
			// z odtworzeniem podgladu. Bez tego zapis profilu albo powrot do karty
			// w trakcie podgladu po cichu oddaje sesji uprawnienia operatora.
			const response = await refreshSession();
			if (!response) throw new Error("refresh failed");
			setAccessToken(response.accessToken);
			const me = await authApi.getMe();
			// Zwykle ta sama osoba. Inna tylko wtedy, gdy ciasteczko refresh należy już
			// do innego konta (logowanie w innej karcie) — publishUser sam przepuszcza
			// bieżącą trasę przez guardy nowej osoby.
			void publishUser(me);
			scheduleRefresh(response.expiresIn);
		} catch {
			setAccessToken(null);
			publishUser(null);
		}
	}, [scheduleRefresh, publishUser]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: queryClient is stable singleton from useQueryClient
	useEffect(() => {
		let cancelled = false;

		void (async () => {
			// Tryb embedded (panel agenta w WebView): token jest WSTRZYKNIETY przez agenta
			// (window.__FM_PANEL__), a WebView NIE ma httpOnly refresh-cookie. silentRefresh
			// zwrocilby null -> catch -> setAccessToken(null) wyzerowalby panel-token, przez
			// co asystent-search lecialby bez Bearera (401 -> "Brak wynikow"). Zostawiamy
			// wstrzyknięty token nietkniety. Parytet z route-guards.ensureAuthenticated.
			if (isEmbedded()) {
				setIsLoading(false);
				return;
			}
			try {
				// silentRefresh ma mutex wspoldzielony z ensureAuthenticated route guardem —
				// przy hard refresh oba wywolania sie spotykaja i robimy jeden /auth/refresh
				const response = await silentRefresh();
				if (cancelled) return;
				if (!response) throw new Error("refresh failed");
				// silentRefresh juz ustawil _accessToken; setAccessToken jest idempotentny
				setAccessToken(response.accessToken);

				// Podglad domykamy PRZED pierwszym /auth/me. Wczesniej bootstrap
				// publikowal tu operatora przez setCurrentUser(me), a `_currentUser` jest
				// wspoldzielone z route guardem: guard, ktory trafil w to okno, widzial
				// niepusta wartosc, POMIJAL `ensureImpersonation()` i decydowal na rolach
				// operatora — dokladnie ta dziura, ktora brama miala zamknac.
				// Przez wspolna brame, nie przez adminApi wprost: guard (`loadUser`) uzywa
				// tego samego mutexu, wiec kto pierwszy, ten startuje, a drugi dolacza do
				// tego samego promise'a zamiast robic druga impersonacje i drugi wpis
				// w audit logu.
				const marker = readImpersonationMarker();
				const impResponse = marker ? await ensureImpersonation() : null;
				if (cancelled) return;
				if (marker && !impResponse) {
					// ensureImpersonation samo wyczyscilo marker — zostajemy na sesji
					// operatora, bez sladu podgladu w UI.
					console.error("[auth] Re-impersonation failed for user", marker.userId);
				}
				if (impResponse) setAccessToken(impResponse.accessToken);

				// Dopiero teraz pytamy o tozsamosc — i publikujemy WYLACZNIE koncowa.
				const me = await authApi.getMe();
				if (cancelled) return;
				void publishUser(me);
				// Cache zhydratowaliśmy w `main.tsx` PRZED poznaniem tożsamości — teraz
				// pierwszy raz wiadomo, czy zapisał go ten sam człowiek. Jeśli nie,
				// leci i z localStorage, i z pamięci.
				assertPersistedCacheOwner(queryClient, me);
				scheduleRefresh((impResponse ?? response).expiresIn);

				if (impResponse) {
					setImpersonationExpiresAt(Date.now() + impResponse.expiresIn * 1000);
					const savedStartedAt = Number(sessionStorage.getItem("impersonating_started_at"));
					setImpersonationStartedAt(
						Number.isFinite(savedStartedAt) && savedStartedAt > 0 ? savedStartedAt : Date.now(),
					);
					queryClient.removeQueries({ queryKey: ["visibility-rules", "admin"] });
					void queryClient.invalidateQueries({ queryKey: ["visibility-rules", "me"] });
					console.debug("[auth] Re-impersonation successful", me.email);
				}
			} catch {
				setAccessToken(null);
				publishUser(null);
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [scheduleRefresh, publishUser]);

	useEffect(() => {
		return () => {
			if (refreshTimerRef.current) {
				clearTimeout(refreshTimerRef.current);
			}
		};
	}, []);

	// Listen for session-expired events from the 401 interceptor
	useEffect(() => {
		const handleSessionExpired = () => {
			if (user) setSessionExpired(true);
		};
		window.addEventListener("auth:session-expired", handleSessionExpired);
		return () => window.removeEventListener("auth:session-expired", handleSessionExpired);
	}, [user]);

	// Listen for token-refreshed events from the silent refresh mutex
	useEffect(() => {
		const handleTokenRefreshed = (e: Event) => {
			const detail = (e as CustomEvent<{ expiresIn: number }>).detail;
			scheduleRefresh(detail.expiresIn);
		};
		window.addEventListener("auth:token-refreshed", handleTokenRefreshed);
		return () => window.removeEventListener("auth:token-refreshed", handleTokenRefreshed);
	}, [scheduleRefresh]);

	// Proactive token check when tab regains focus (laptop wake, tab switch)
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState !== "visible") return;
			const exp = getTokenExpiry();
			if (!exp) return;
			const now = Date.now() / 1000;
			if (exp - now < 60) {
				refreshToken();
			}
		};
		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, [refreshToken]);

	const handleReauthenticated = useCallback(async () => {
		setSessionExpired(false);
		const me = await authApi.getMe();
		// Modal pozwala zalogować się INNYM kontem — publishUser czyści wtedy cache,
		// przepuszcza bieżącą trasę przez guardy nowej osoby (bez tego strona admina
		// zostawała pod kontem bez uprawnień i wysyłała /admin/* → 403) i montuje
		// drzewo od nowa; ta sama osoba zachowuje dane i stan ekranu.
		const switched = publishUser(me);
		assertPersistedCacheOwner(queryClient, me);
		// After session-expired reauth the user typically stays on the same page, so
		// refetchType: "none" defers refetches until the affected components decide
		// (remount, focus, manual). Avoids the same stale-observer race as the
		// impersonation flows — see startImpersonation note for the full picture.
		await queryClient.invalidateQueries({ refetchType: "none" });
		await switched;
	}, [queryClient, publishUser]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: user state changes whenever token changes — triggers recalculation of impersonation status from module-scoped token
	const tokenClaims = useMemo(() => {
		const none = { isImpersonating: false, impersonatedBy: null as number | null };
		const token = getAccessToken();
		if (!token) return none;
		try {
			const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
			const payload = JSON.parse(atob(base64));
			return {
				isImpersonating: payload.impersonating === true,
				impersonatedBy:
					typeof payload.impersonated_by === "number" ? payload.impersonated_by : null,
			};
		} catch {
			return none;
		}
	}, [user]);
	const isImpersonating = tokenClaims.isImpersonating;

	// Stan `user` to jedyne zrodlo tozsamosci w aplikacji, wiec jeden efekt
	// pokrywa login, silent refresh, impersonacje i logout. Podczas podgladu
	// `user` trzyma podgladane konto, a operator jedzie tagiem impersonated_by.
	useEffect(() => {
		syncSentryUser(user, tokenClaims.isImpersonating ? tokenClaims.impersonatedBy : null);
	}, [user, tokenClaims]);

	return (
		<AuthContext.Provider
			value={{
				user,
				isLoading,
				isAuthenticated: user !== null,
				isImpersonating,
				impersonationExpiresAt,
				impersonationStartedAt,
				login,
				logout,
				refreshToken,
				startImpersonation,
				stopImpersonation,
			}}
		>
			{/* Klucz = epoka tożsamości: zmiana osoby montuje drzewo od zera, więc żaden
			    komponent nie zachowuje wyniku ani lokalnego stanu poprzedniej. Na czas
			    rozstrzygania trasy dla nowej osoby drzewa nie ma na ekranie (publishUser). */}
			{identitySwitching ? (
				<div className="flex h-dvh items-center justify-center">
					<div className="size-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
				</div>
			) : (
				<Fragment key={identityEpoch}>{children}</Fragment>
			)}
			<SessionExpiredModal open={sessionExpired} onReauthenticated={handleReauthenticated} />
		</AuthContext.Provider>
	);
}

export function useAuthContext(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (!ctx) {
		throw new Error("useAuthContext must be used within AuthProvider");
	}

	return ctx;
}

/** Zalogowany użytkownik albo `null` — bez rzucania poza `AuthProvider` (testy komponentów). */
export function useOptionalAuthUser(): User | null {
	return useContext(AuthContext)?.user ?? null;
}
